import * as React from "react";
import { CaseRunClient, FamilialSearchResultItemModel, KinshipRelationship, FamilialSearchParameters } from "@app/api2/api";
import moment from "moment";
import { BPColumn } from "@common/BPReactTable";
import { parseParameters, toSci } from "@common/Utils";
import { useDialogContext } from "@app/components/React/Hooks/DialogContext";
import { SampleEdit } from "@app/components/React/Samples/SampleEdit";
import { GenderSelectorSelect } from "../../../RunTypes/Brutus/GenderSelector";
import { caseRunClient } from "@app/api2/Clients";
import { BPReactTable2, convertColumns, useBpReactTable2 } from "@app/components/React/Common/DataTable/BPReactTable2";
import Dashboard from "../../../../../../../pages/dashboard";
import '../../../../../../../index.css'
import { FamilialSearchHitDetails } from "./FamilialSearchHitDetails";

interface FamilialSearchResultsProps {
    labId: number;
    caseId: number
    caseRunId: number;
    forPrint?: boolean;
}

interface FamilialSearchResultItemModelEx extends FamilialSearchResultItemModel {
    // matchDetails: ResultRow[];
}

export const FamilialSearchResults: React.FC<FamilialSearchResultsProps> = ({ labId, caseId, caseRunId, forPrint }) => {
    const [matches, setMatches] = React.useState<FamilialSearchResultItemModelEx[]>([]);
    const [parameters, setParameters] = React.useState<FamilialSearchParameters | undefined>(undefined);
    const clientRef = React.useRef<CaseRunClient>(caseRunClient);
    


    const columns2: Array<BPColumn<FamilialSearchResultItemModel>> = [
        {
            Header: window.translation.common.case,
            accessor: 'otherCaseName',
            disableSortBy: true,
            width: "15%",
            // width: "140px",
            Cell: item => <a style={dotdotdotStyle} title={item.value} href={item.row.original.otherCaseUrl}>{item.value}</a>,
        },
        {
            Header: window.translation.common.created,
            accessor: 'otherCaseDate',
            disableSortBy: true,
            Cell: item => <span>{moment(item.value).format("MM/DD/YYYY")}</span>,
            // width: "110px",
            width: "10%",
        },
        {
            Header: 'Relationship',
            accessor: 'relationship',
            Cell: cell => cell.value == KinshipRelationship.ParentChild ? 'Parent/Child' : cell.value == KinshipRelationship.FullSiblings ? 'Full Siblings' : 'Ext. Family',
            // width: "60px",
            width: "8%",
        },
        {
            Header: window.translation.brutus.relative1,
            accessor: 'uid1Name',
            Cell: item => <>
                {<GenderSelectorSelect showLabel={false} showIcon={true} value={item.row.original.uid1Sample?.sex} name={'sex'} readOnly></GenderSelectorSelect>}
                {item.row.original.uid1Sample &&
                    <a href="" onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();

                        return dlg.show({
                            header: 'Edit Sample',
                            content: <SampleEdit dialogMode onSave={async () => {
                                // await refresh();
                                return dlg.hide();
                            }} onCancel={dlg.hide} sampleId={item.row.original.uid1Sample.id} labId={labId} caseId={item.row.original.uid1CaseId} />,
                        });
                    }}>{item.value}</a>
                }
                {!item.row.original.uid1Sample &&
                    <a style={dotdotdotStyle}
                        title={`${item.value} (${item.row.original.uid1CaseName})`}
                        href={item.row.original.uid1Url}>{item.value}</a>
                }
            </>
        },
        {
            Header: window.translation.brutus.relative2,
            accessor: 'uid2Name',
            Cell: item => <>
                {<GenderSelectorSelect showLabel={false} showIcon={true} value={item.row.original.uid2Sample?.sex} name={'sex'} readOnly></GenderSelectorSelect>}
                {!item.row.original.uid2Sample &&
                    <a style={dotdotdotStyle} title={`${item.value} (${item.row.original.uid2CaseName})`} href={item.row.original.uid2Url}>{item.value}</a>
                }
                {item.row.original.uid2Sample &&
                    <a href="" onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();

                        return dlg.show({
                            header: 'Edit Sample',
                            content: <SampleEdit dialogMode onSave={dlg.hide} onCancel={dlg.hide} sampleId={item.row.original.uid2Sample.id} labId={labId} caseId={item.row.original.uid2CaseId} />,
                        });
                    }}>{item.value}</a>
                }
            </>,
        },
        {
            Header: window.translation.caseRun.commonLoci,
            accessor: 'commonLoci',
            // width: "60px",
            width: "5%",
        },
        {
            Header: window.translation.caseRunView.matchingLoci,
            accessor: 'matchingLoci',
            // width: "60px",
            width: "5%",
        },
        {
            Header: window.translation.caseRun.mac,
            accessor: 'mac',
            Cell: cell => cell.value.toPrecision(2),
            // width: "60px",
            width: "5%",
        },
        ...parameters?.kinParentChildEnabled ? [{
            Header: window.translation.brutus.parentChildCRI,
            accessor: 'parentChildProb',
            Cell: item => <span title={toSci(item.value, 4)}>{toSci(item.value, 4)}</span>,
            width: "8%",
            // hidden: !parameters?.kinParentChildEnabled
        }] : [] as any,
        ...parameters?.kinSiblingsEnabled ? [{
            Header: window.translation.brutus.fullSibsCRI,
            accessor: 'fullSiblingsProb',
            Cell: item => <span title={toSci(item.value, 4)}>{toSci(item.value, 4)}</span>,
            width: "8%",
            // hidden: !parameters?.kinSiblingsEnabled 
        }] : [] as any,
        ...parameters?.kinExtFamilyEnabled ? [{
            Header: window.translation.brutus.extFamilyCRI,
            accessor: 'extFamilyProb',
            Cell: item => <span title={toSci(item.value, 4)}>{toSci(item.value, 4)}</span>,
            width: "8%",
            // hidden: !parameters?.kinExtFamilyEnabled 
        }] : [] as any
    ];

    const table = useBpReactTable2<FamilialSearchResultItemModelEx>({

        columnDefs: convertColumns(columns2),
        queryKeyPrefix: "FamilialSearchResults",
        query: async (paging, sorting, searchText, filters) => {
            let pageData = await clientRef.current.getFamilialSearchResults(labId, caseId, caseRunId, paging.pageSize, paging.pageIndex + 1, sorting[0]?.id, sorting[0]?.desc ? 'desc' : 'asc');
            return { rows: pageData.items, rowCount: pageData.totalRows };
        },
        getRowCanExpand: React.useCallback((row) => true, []),
        renderSubComponent: ((row) => <FamilialSearchHitDetails key={row.row.original.resultId} match={row.row.original} resultId={row.row.original.resultId} labId={labId} caseId={caseId} caseRunId={caseRunId} />)
        

    });


    const refresh = React.useCallback(async (p: { page?: number, sortBy?: string, sortDirection?: string, perPage?: number, filters?: Array<{ id: string, value: any }> }) => {
        let pageData = await clientRef.current.getFamilialSearchResults(labId, caseId, caseRunId, p.perPage, p.page, p.sortBy, p.sortDirection);
        setMatches(pageData.items.map(m => { return { ...m, matchDetails: [] } }));
        return pageData;
    }, [labId, caseId, caseRunId]);

    React.useEffect(() => {
        const fetchParameters = async () => {
            let data = await clientRef.current.results(labId, caseId, caseRunId);
            var parsed = parseParameters<FamilialSearchParameters>(data.detail.parameters);
            setParameters(parsed);
        };

        fetchParameters();
    }, [labId, caseId, caseRunId]);

    const dlg = useDialogContext();

    const dotdotdotStyle: React.CSSProperties = { 'overflow': 'hidden', 'textOverflow': 'ellipsis', whiteSpace: "nowrap" };


    return (<>
        <BPReactTable2 {...table} fixed singleLine/>
        <Dashboard caseId={caseId.toString()} caseRunId={caseRunId.toString()} labId={labId.toString()}/>
        {/* <BPDataTable3
            forPrint={forPrint}
            storeKey={"FamilialSearchResults"}
            fixed
            singleLine
            columns={columns2}
            dataSource={p => refresh(p)}
            expandableRowsComponent={((row) => <FamilialSearchHitDetails match={row} resultId={row.resultId} labId={labId} caseId={caseId} caseRunId={caseRunId} />)}
        /> */}
    </>
    );
};